import { Session } from '@supabase/supabase-js';
import { useRouter } from '@tanstack/react-router';
import { jwtDecode } from 'jwt-decode';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { authApi } from '@/features/auth/api/auth-api';
import {
  useResetPassword,
  useSignIn,
  useSignOut,
  useSignUp,
  useUpdatePassword,
} from '@/features/auth/api/auth-mutations';
import { AuthContext } from '@/features/auth/context/auth-context';
import { User } from '@/features/auth/types/auth-types';
import { UserRole } from '@/types';
import { supabaseClient } from '@/utils/supabase-client';

interface UserOrganisation {
  organisation_id: string;
  role: UserRole;
}

interface JWTClaims {
  user_organisations: UserOrganisation[];
  is_superuser: boolean;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isSuperUser, setIsSuperUser] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [userOrganisations, setUserOrganisations] = useState<
    UserOrganisation[]
  >([]);

  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const { mutateAsync: signIn } = useSignIn();
  const { mutateAsync: signUp } = useSignUp();
  const { mutateAsync: signOut } = useSignOut();
  const { mutateAsync: resetPassword } = useResetPassword();
  const updatePassword = useUpdatePassword();

  // Monitor auth state changes
  useEffect(() => {
    const extractSessionData = (session: Session | null) => {
      if (!session) {
        setUserOrganisations([]);
        setIsSuperUser(false);
        setIsAuthenticated(false);
        return;
      }

      const user = session.user;

      if (user.email === undefined) {
        setUserOrganisations([]);
        setIsSuperUser(false);
        setIsAuthenticated(false);
        return;
      }

      setUser(user as User);

      try {
        const claims = jwtDecode<JWTClaims>(session.access_token);
        setUserOrganisations(claims.user_organisations);
        setIsSuperUser(claims.is_superuser);
        setIsAuthenticated(true);
      } catch {
        setUserOrganisations([]);
        setIsSuperUser(false);
        setIsAuthenticated(false);
      }
    };

    authApi
      .getSession()
      .then(({ data: { session } }) => {
        extractSessionData(session);
        setIsLoading(false);
      })
      .catch(() => {
        toast.error('Failed to get session');
        setIsLoading(false);
      });

    const {
      data: { subscription },
    } = supabaseClient.auth.onAuthStateChange((_event, session) => {
      extractSessionData(session);
      setIsLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  // (TODO: these might be better in a dedicated tenant provider?)
  const hasRole = (
    requiredRoles: UserRole | UserRole[],
    tenantId: string,
  ): boolean => {
    if (!user) return false;

    const roles = Array.isArray(requiredRoles)
      ? requiredRoles
      : [requiredRoles];

    return userOrganisations.some((org) => {
      const matchesTenant = org.organisation_id === tenantId;
      const hasRequiredRole = roles.includes(org.role);
      return matchesTenant && hasRequiredRole;
    });
  };

  // TODO: better in a dedicated tenant provider?
  const getUserRole = (tenantId?: string): UserRole | null => {
    if (!user) return null;

    const org = userOrganisations.find(
      (org) => !tenantId || org.organisation_id === tenantId,
    );

    return org?.role ?? null;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        isSuperUser,
        isLoading,
        hasRole,
        getUserRole,
        signIn,
        signUp,
        signOut,
        resetPassword,
        updatePassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
